/**
 * Модуль для загрузки и отображения статуса Errorborn
 */

const ERRORBORN_STATUS_CACHE_TTL_MS = 30000; // 30 секунд

/**
 * Создаёт менеджер для работы со статусом Errorborn
 */
export function createErrorbornStatusManager({
  safeFetch,
  buildAuthHeaders,
  getApiUrl,
  getTelegramWebApp,
  getErrorbornStatus,
  setErrorbornStatus,
  getErrorbornStatusTimestamp,
  setErrorbornStatusTimestamp,
  renderErrorbornQuestCard,
  renderErrorbornScreen,
  loadHomeScreenData,
  documentRef = document,
}) {
  let isClaiming = false;

  /**
   * Загружает статус Errorborn с кэшированием
   * @param {boolean} force - принудительно обновить, игнорируя кэш
   */
  async function fetchErrorbornStatus(force = false) {
    const timestamp = Date.now();
    const cachedStatus = getErrorbornStatus();
    const cachedTimestamp = getErrorbornStatusTimestamp();

    if (!force && cachedStatus && timestamp - cachedTimestamp < ERRORBORN_STATUS_CACHE_TTL_MS) {
      return cachedStatus;
    }

    const telegramWebApp = getTelegramWebApp();
    if (!telegramWebApp || !telegramWebApp.initData) {
      return null;
    }

    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/user/errorborn`, {
        headers: buildAuthHeaders(),
      });

      if (!response.success || !response.data?.success) {
        console.warn(
          "Error loading Errorborn status:",
          response.error || response.data?.error,
        );
        return null;
      }

      const status = response.data.status;
      setErrorbornStatus(status);
      setErrorbornStatusTimestamp(timestamp);
      renderErrorbornQuestCard(status);
      return status;
    } catch (error) {
      console.error("fetchErrorbornStatus error:", error);
      return null;
    }
  }

  /**
   * Загружает экран Errorborn
   */
  async function loadErrorbornScreen(force = false) {
    const container = documentRef.getElementById("errorborn-screen-content");
    if (!container) return;

    container.innerHTML = '<div class="loading">Загрузка хроник Errorborn...</div>';

    const status = await fetchErrorbornStatus(force || !getErrorbornStatus());
    if (!status) {
      container.innerHTML =
        '<div class="error">Не удалось получить данные квеста. Попробуйте позже.</div>';
      return;
    }

    // Используем renderErrorbornScreen из модуля
    renderErrorbornScreen(status, status.storyText || []);

    // Если история не просмотрена, отмечаем её
    if (!status.storySeen) {
      markErrorbornStorySeen().catch((error) =>
        console.warn("markErrorbornStorySeen error:", error),
      );
    }
  }

  /**
   * Призывает Errorborn
   */
  async function claimErrorbornReward() {
    if (isClaiming) {
      console.log("⏳ Запрос уже выполняется, пропускаем...");
      return;
    }

    isClaiming = true;
    const telegramWebApp = getTelegramWebApp();

    try {
      if (telegramWebApp?.showProgress) {
        telegramWebApp.showProgress();
      }

      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/user/errorborn/claim`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...buildAuthHeaders(),
        },
        body: JSON.stringify({}),
      });

      if (!response.success) {
        const error = response.error || "Не удалось призвать Errorborn";
        if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert(error);
        } else {
          alert(error);
        }
        return;
      }

      if (!response.data?.success) {
        const error =
          response.data?.error || "Не удалось призвать Errorborn";
        if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert(error);
        } else {
          alert(error);
        }
        return;
      }

      const status = response.data.status;
      setErrorbornStatus(status);
      setErrorbornStatusTimestamp(Date.now());

      // Обновляем UI
      renderErrorbornQuestCard(status);
      renderErrorbornScreen(status, status.storyText || []);

      // Обновляем главный экран
      if (loadHomeScreenData && typeof loadHomeScreenData === "function") {
        await loadHomeScreenData();
      }

      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert(
          "✨ Errorborn откликнулся на твой призыв! Найди его в коллекции.",
        );
      }
    } catch (error) {
      console.error("claimErrorbornReward error:", error);
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert(
          "Ошибка призыва Errorborn: " +
            (error.message || "Неизвестная ошибка"),
        );
      }
    } finally {
      isClaiming = false;
      if (telegramWebApp?.hideProgress) {
        telegramWebApp.hideProgress();
      }
    }
  }

  /**
   * Отмечает историю Errorborn как просмотренную
   */
  async function markErrorbornStorySeen() {
    const status = getErrorbornStatus();
    if (!status || status.storySeen) {
      return;
    }

    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/user/errorborn/story-seen`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...buildAuthHeaders(),
        },
        body: JSON.stringify({}),
      });

      if (response.success && response.data?.success) {
        const newStatus = response.data.status;
        setErrorbornStatus(newStatus);
        setErrorbornStatusTimestamp(Date.now());
        renderErrorbornQuestCard(newStatus);
      }
    } catch (error) {
      console.warn("markErrorbornStorySeen failed:", error);
    }
  }

  return {
    fetchErrorbornStatus,
    loadErrorbornScreen,
    claimErrorbornReward,
    markErrorbornStorySeen,
  };
}


